import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  Avatar,
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Modal,
  PageHeader,
  Select,
  Spinner,
  StatusBadge,
} from '@/components/ui'
import { useQuery } from '@/hooks/useQuery'
import { ApiError, api } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { dateTime, matchTypeLabel, relativeTime, sentence } from '@/lib/format'
import type { Ack, Introduction, IntroductionOutcome, MatchParty, Page } from '@/types'

const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'awaiting_consent', label: 'Awaiting consent' },
  { value: 'ready', label: 'Ready to send' },
  { value: 'sent', label: 'Sent' },
  { value: 'connected', label: 'Connected' },
  { value: 'completed', label: 'Completed' },
  { value: 'abandoned', label: 'Abandoned' },
]

const OUTCOMES: Array<{ value: IntroductionOutcome; label: string }> = [
  { value: 'conversing', label: 'They are talking' },
  { value: 'deal_made', label: 'It led to real business' },
  { value: 'no_response', label: 'No response' },
  { value: 'declined', label: 'One of them declined' },
  { value: 'unknown', label: 'Unknown' },
]

export function IntroductionsPage() {
  const { canApprove } = useAuth()
  const [status, setStatus] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [outcomeFor, setOutcomeFor] = useState<Introduction | null>(null)

  const query = useQuery(
    useCallback(
      () =>
        api.get<Page<Introduction>>('/introductions', {
          status: status || undefined,
          limit: 60,
        }),
      [status],
    ),
    [status],
  )

  async function send(introduction: Introduction) {
    setBusy(introduction.id)
    setNotice(null)
    try {
      const ack = await api.post<Ack>(`/introductions/${introduction.id}/send`)
      setNotice(ack.detail ?? 'Introduction queued.')
      query.reload()
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Could not send.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader
        title="Introductions"
        subtitle="Two-key consent: names are exchanged only once both people have said yes."
        action={<Select label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />}
      />

      {notice && (
        <p className="mb-3 rounded-md px-3 py-2 text-sm text-ink" style={{ background: 'var(--surface-2)' }} role="status">
          {notice}
        </p>
      )}

      {query.loading && !query.data ? (
        <Spinner />
      ) : query.error ? (
        <Card>
          <ErrorNote message={query.error} onRetry={query.reload} />
        </Card>
      ) : !query.data?.items.length ? (
        <Card>
          <EmptyState
            title="No introductions yet"
            hint="Approve a match, then ask both people for consent — introductions appear here."
            action={
              <Link to="/opportunities" className="btn-primary">
                Review opportunities
              </Link>
            }
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {query.data.items.map((introduction) => (
            <IntroCard
              key={introduction.id}
              introduction={introduction}
              busy={busy === introduction.id}
              canApprove={canApprove}
              onSend={send}
              onRecordOutcome={setOutcomeFor}
            />
          ))}
        </div>
      )}

      <OutcomeModal
        introduction={outcomeFor}
        onClose={() => setOutcomeFor(null)}
        onSaved={() => {
          setOutcomeFor(null)
          query.reload()
        }}
      />
    </>
  )
}

function IntroCard({
  introduction,
  busy,
  canApprove,
  onSend,
  onRecordOutcome,
}: {
  introduction: Introduction
  busy: boolean
  canApprove: boolean
  onSend: (introduction: Introduction) => void
  onRecordOutcome: (introduction: Introduction) => void
}) {
  const bothConsented =
    introduction.consent_a_status === 'granted' && introduction.consent_b_status === 'granted'
  const sent = introduction.status === 'sent' ||
    introduction.status === 'connected' ||
    introduction.status === 'completed'

  return (
    <article className="card flex flex-col">
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={introduction.status} />
          <Badge tone="info">{matchTypeLabel(introduction.match_type)}</Badge>
          <span className="text-xs tabular text-ink-faint">Score {introduction.score}</span>
        </div>
        <span className="shrink-0 text-xs text-ink-faint">
          {relativeTime(introduction.created_at)}
        </span>
      </header>

      <div className="grid grid-cols-2 gap-3 px-4 py-3">
        <ConsentSide
          party={introduction.person_a}
          status={introduction.consent_a_status}
          at={introduction.consent_a_at}
        />
        <ConsentSide
          party={introduction.person_b}
          status={introduction.consent_b_status}
          at={introduction.consent_b_at}
        />
      </div>

      {introduction.outcome !== 'unknown' && (
        <div className="px-4 pb-3">
          <Badge
            tone={
              introduction.outcome === 'deal_made'
                ? 'good'
                : introduction.outcome === 'conversing'
                  ? 'info'
                  : 'neutral'
            }
            icon={introduction.outcome === 'deal_made' ? '✓' : undefined}
          >
            {sentence(introduction.outcome)}
          </Badge>
          {introduction.outcome_note && (
            <p className="mt-1.5 text-xs text-ink-muted">{introduction.outcome_note}</p>
          )}
        </div>
      )}

      <footer className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-3">
        <Link
          to={`/opportunities/${introduction.match_id}`}
          className="text-xs text-ink-muted hover:text-ink"
        >
          View match →
        </Link>

        {canApprove && (
          <div className="flex gap-2">
            {sent && (
              <button
                type="button"
                className="btn-secondary px-2.5 py-1 text-xs"
                onClick={() => onRecordOutcome(introduction)}
              >
                Record outcome
              </button>
            )}
            {!sent && (
              <button
                type="button"
                className="btn-primary px-2.5 py-1 text-xs"
                disabled={busy || !bothConsented}
                title={
                  bothConsented
                    ? undefined
                    : 'Both people must consent before an introduction can be sent'
                }
                onClick={() => onSend(introduction)}
              >
                {busy ? '…' : 'Send introduction'}
              </button>
            )}
          </div>
        )}
      </footer>
    </article>
  )
}

function ConsentSide({
  party,
  status,
  at,
}: {
  party: MatchParty
  status: string
  at: string | null
}) {
  return (
    <div className="rounded-md border border-line px-3 py-2.5">
      <Link to={`/contacts/${party.contact_id}`} className="flex items-center gap-2 hover:underline">
        <Avatar name={party.name} size={26} />
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-ink">{party.name}</span>
          <span className="block truncate text-xs text-ink-muted">{party.headline || '—'}</span>
        </span>
      </Link>
      <div className="mt-2 flex items-center gap-2">
        <StatusBadge status={status} />
        {at && <span className="text-xs text-ink-faint">{relativeTime(at)}</span>}
      </div>
    </div>
  )
}

function OutcomeModal({
  introduction,
  onClose,
  onSaved,
}: {
  introduction: Introduction | null
  onClose: () => void
  onSaved: () => void
}) {
  const [outcome, setOutcome] = useState<IntroductionOutcome>('conversing')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    if (!introduction) return
    setBusy(true)
    setError(null)
    try {
      await api.post(`/introductions/${introduction.id}/outcome`, { outcome, note: note || null })
      setNote('')
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={introduction !== null}
      title="Record the outcome"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save outcome'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-muted">
          This is the signal the whole funnel is measured on — and what teaches the engine which
          match types actually work.
        </p>

        {introduction && (
          <p className="text-sm text-ink">
            {introduction.person_a.name} ⇄ {introduction.person_b.name}
            {introduction.sent_at && (
              <span className="block text-xs text-ink-faint">
                Sent {dateTime(introduction.sent_at)}
              </span>
            )}
          </p>
        )}

        <Field label="What happened?">
          <select
            className="field"
            value={outcome}
            onChange={(event) => setOutcome(event.target.value as IntroductionOutcome)}
          >
            {OUTCOMES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Notes" hint="Optional context for whoever reviews this later.">
          <textarea
            className="field h-24"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>

        {error && (
          <p className="text-sm" style={{ color: 'var(--critical)' }} role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  )
}
